import {
  ActionsObservable,
  combineEpics,
  StateObservable
} from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, mergeMap } from 'rxjs/operators';
import axios from '../../axios-pod';
import { IState } from '../../models/states';
import actionTypes from '../../utilities/actionTypes';
import { isEmpty } from '../../utilities/utilities';
import { setUserFilters } from '../actions/filterActions';
import {
  addUserSuccess,
  authLogout,
  authSuccess,
  changePasswordSuccess,
  deleteUserSuccess,
  fetchUsersSuccess,
  IAddUser,
  IAuthCheckState,
  IBasicLogin,
  IChangePassword,
  IDeleteUser,
  IFetchUsers,
  IForgotPassword,
  ILogin,
  ILogout,
  ISetPassword,
  IUpdateUser,
  logout,
  updateUserChallengeAndSession,
  updateUserSuccess
} from '../actions/userActions';

const setAuthorizationToken = (token: string) => {
  if (token.length !== 0) {
    axios.defaults.headers.common.Authorization = token;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const onDeleteUser = (action$: ActionsObservable<IDeleteUser>) =>
  action$.ofType(actionTypes.DELETE_USER).pipe(
    exhaustMap((action: IDeleteUser) => {
      return from(axios.delete(`user/${action.userId}`)).pipe(
        mergeMap(_ => {
          const result: any[] = [deleteUserSuccess(action.userId)];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );

export const onLogin = (action$: ActionsObservable<ILogin>) =>
  action$.ofType(actionTypes.LOGIN).pipe(
    exhaustMap((action: ILogin) => {
      axios.defaults.baseURL = globalThis.UI_CONFIG.backend_url;
      return from(axios.post(`/login`, action.payload)).pipe(
        mergeMap(res => {
          const token = res.data.result.token;
          const user = res.data.result;
          localStorage.setItem('podToken', token);
          localStorage.setItem('podUser', JSON.stringify(user));
          setAuthorizationToken(token);

          const result: any[] = [authSuccess(user, token)];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );
export const onBasicLogin = (action$: ActionsObservable<IBasicLogin>) =>
  action$.ofType(actionTypes.BASIC_LOGIN).pipe(
    exhaustMap((action: IBasicLogin) => {
      axios.defaults.baseURL = globalThis.UI_CONFIG.backend_url;
      return from(axios.post(`/basic_login`, action.payload)).pipe(
        mergeMap((res: any) => {
          if (res.data.result.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
            // set challenge and user sess
            return [updateUserChallengeAndSession(res.data.result)];
          } else {
            const token = res.data.result.token;
            const user = res.data.result;
            localStorage.setItem('podToken', token);
            localStorage.setItem('podUser', JSON.stringify(user));
            setAuthorizationToken(token);
            const result: any[] = [authSuccess(user, token)];
            return result;
          }
        }),
        catchError(_ => of())
      );
    })
  );

export const onFetchUsers = (
  action$: ActionsObservable<IFetchUsers>,
  state$: StateObservable<IState>
) =>
  action$.ofType(actionTypes.FETCH_USERS).pipe(
    exhaustMap((action: IFetchUsers) => {
      return from(
        axios.get(`users`, {
          params: { userId: state$.value.user.userInfo.userId }
        })
      ).pipe(
        mergeMap(res => {
          const result: any[] = [
            fetchUsersSuccess(res.data.result),
            setUserFilters(res.data.result)
          ];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );
export const onLogout = (
  action$: ActionsObservable<ILogout>,
  state$: StateObservable<IState>
) =>
  action$.ofType(actionTypes.LOGOUT).pipe(
    mergeMap(() => {
      axios.defaults.baseURL = globalThis.UI_CONFIG.backend_url;
      const state = state$.value;
      localStorage.removeItem('podToken');
      localStorage.removeItem('podUser');
      localStorage.removeItem('podSelectedScreen');
      localStorage.removeItem('podFilters');
      setAuthorizationToken('');
      const payload = {
        user_id: state.user.userInfo ? state.user.userInfo.userId : ''
      };
      axios.post(`/logout`, payload);
      return [authLogout()];
    }),
    catchError(_ => of())
  );
export const onAddUser = (action$: ActionsObservable<IAddUser>) =>
  action$.ofType(actionTypes.ADD_USER).pipe(
    exhaustMap((action: IAddUser) => {
      const payload = action.payload;
      const params = {
        name: payload.name,
        email: payload.email,
        role: payload.role,
        screens: payload.screens,
        is_active: payload.status === 'active' ? true : false
        // jira_user_name: payload.jiraUserName
      };
      return from(axios.post(`user`, params)).pipe(
        mergeMap((res: any) => {
          const result: any[] = [addUserSuccess(res.data.result)];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );

export const onChangePassword = (action$: ActionsObservable<IChangePassword>) =>
  action$.ofType(actionTypes.CHANGE_PASSWORD).pipe(
    exhaustMap((action: IChangePassword) => {
      const payload = action.payload;
      return from(axios.post(`change_password`, payload)).pipe(
        mergeMap((res: any) => {
          const result: any[] = [changePasswordSuccess(res.data.result)];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );

export const onForgotPassword = (action$: ActionsObservable<IForgotPassword>) =>
  action$.ofType(actionTypes.FORGOT_PASSWORD).pipe(
    exhaustMap((action: IForgotPassword) => {
      const payload = action.payload;
      return from(axios.post(`forgot_password`, payload)).pipe(
        mergeMap((res: any) => {
          const result: any[] = [];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );

export const onSetPassword = (action$: ActionsObservable<ISetPassword>) =>
  action$.ofType(actionTypes.SET_PASSWORD).pipe(
    exhaustMap((action: ISetPassword) => {
      const payload = action.payload;
      return from(axios.post(`set_password`, payload)).pipe(
        mergeMap((res: any) => {
          const result: any[] = [];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );

export const onUpdateUser = (action$: ActionsObservable<IUpdateUser>) =>
  action$.ofType(actionTypes.UPDATE_USER).pipe(
    exhaustMap((action: IUpdateUser) => {
      const payload = action.payload;
      const params = {
        user_id: payload.userId,
        name: payload.name,
        email: payload.email,
        role: payload.role,
        screens: payload.screens,
        is_active: payload.status === 'active' ? true : false
        // jira_user_name: payload.jiraUserName
      };
      return from(axios.put(`/user/${payload.userId}`, params)).pipe(
        mergeMap((res: any) => {
          const result: any[] = [updateUserSuccess(res.data.result)];
          return result;
        }),
        catchError(_ => of())
      );
    })
  );
export const onAuthCheck = (action$: ActionsObservable<IAuthCheckState>) =>
  action$.ofType(actionTypes.AUTH_CHECK).pipe(
    mergeMap(() => {
      const token = localStorage.getItem('podToken');
      const userInfo = JSON.parse(localStorage.getItem('podUser'));
      const result: any[] = [];
      if (!token || token.length === 0 || isEmpty(userInfo)) {
        result.push(logout());
      } else {
        setAuthorizationToken(token);
        result.push(authSuccess(userInfo, token));
      }
      return result;
    }),
    catchError(_ => of())
  );

export default combineEpics(
  onDeleteUser,
  onLogin,
  onFetchUsers,
  onLogout,
  onAddUser,
  onUpdateUser,
  onAuthCheck,
  onBasicLogin,
  onChangePassword,
  onForgotPassword,
  onSetPassword
);
